import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { pushPlugin, nativePlatform, PUSH_TOKEN_KEY } from "@/lib/nativePush";

// Tells the server how notification setup went, so a phone that never
// registers can be diagnosed from the backend logs.
function report(stage, detail = "") {
  api.post("/me/push-status", { stage, detail: String(detail).slice(0, 300) }).catch(() => {});
}

// Registers the phone app for "your turn" notifications and opens the game
// a tapped notification is about. Does nothing in a normal browser.

export default function NativePush() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const userId = user?.id;

  // Tapping a notification opens the game it's about.
  useEffect(() => {
    const push = pushPlugin();
    if (!push) return undefined;
    let handle;
    Promise.resolve(push.addListener("pushNotificationActionPerformed", (ev) => {
      const url = ev?.notification?.data?.url;
      if (typeof url === "string" && url.startsWith("/")) navRef.current(url);
    })).then((h) => { handle = h; }).catch(() => {});
    return () => { try { handle?.remove(); } catch { /* ignore */ } };
  }, []);

  // Signed in: ask permission once, register, and send the token to the server.
  useEffect(() => {
    const push = pushPlugin();
    if (!userId) return undefined;
    if (!push) {
      if (window.Capacitor?.isNativePlatform?.()) report("no_plugin", Object.keys(window.Capacitor.Plugins || {}).join(","));
      return undefined;
    }
    let cancelled = false;
    const handles = [];
    (async () => {
      try {
        handles.push(await push.addListener("registration", async ({ value }) => {
          if (cancelled || !value) return;
          try { localStorage.setItem(PUSH_TOKEN_KEY, value); } catch { /* ignore */ }
          try {
            await api.post("/me/push-token", { token: value, platform: nativePlatform() });
          } catch (e) {
            report("token_post_failed", e?.message || e);
          }
        }));
        handles.push(await push.addListener("registrationError", (err) => {
          report("registration_error", err?.error || JSON.stringify(err));
        }));
        let perm = await push.checkPermissions();
        if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
          perm = await push.requestPermissions();
        }
        report("permission", perm.receive);
        if (perm.receive !== "granted" || cancelled) return;
        try {
          await push.createChannel({
            id: "turns", name: "Your turn", description: "When it's your move or someone challenges you",
            importance: 4, visibility: 1,
          });
        } catch { /* iOS has no channels */ }
        await push.register();
      } catch (e) {
        report("setup_failed", e?.message || e);
      }
    })();
    return () => {
      cancelled = true;
      handles.forEach((h) => { try { h.remove(); } catch { /* ignore */ } });
    };
  }, [userId]);

  return null;
}
